/**
 * XML Formatter Service
 * Implements business logic for XML formatting, minification, and validation
 */

import { EncodingOperation, EncoderToolType } from '../types';
import { ProcessingError, ValidationError } from '../utils/errorHandler';
import { CommonValidator } from '../validation/commonValidation';

export class XmlFormatterService {
  /**
   * Format XML with pretty printing
   */
  static async format(
    xml: string,
    options: {
      indentSize?: number;
    } = {}
  ): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(xml,
        CommonValidator.getValidationRules(EncoderToolType.XML_FORMATTER)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, EncoderToolType.XML_FORMATTER, validation.suggestion);
      }

      // Validate XML
      const parser = new DOMParser();
      const doc = parser.parseFromString(xml, 'text/xml');
      const parseError = doc.querySelector('parsererror');

      if (parseError) {
        throw new ValidationError(
          `Invalid XML: ${parseError.textContent || 'Parse error'}`,
          EncoderToolType.XML_FORMATTER,
          'Check for unclosed tags or invalid characters'
        );
      }

      // Format XML
      const indent = ' '.repeat(options.indentSize || 2);
      const formatted = this.formatXmlString(xml, indent);

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.XML_FORMATTER,
        operationType: 'encode',
        inputData: xml,
        outputData: formatted,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          mode: 'format',
          indentSize: options.indentSize || 2,
          originalLength: xml.length,
          formattedLength: formatted.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to format XML',
        EncoderToolType.XML_FORMATTER,
        'Please check your XML syntax'
      );
    }
  }

  /**
   * Minify XML (remove whitespace)
   */
  static async minify(xml: string): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(xml,
        CommonValidator.getValidationRules(EncoderToolType.XML_FORMATTER)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, EncoderToolType.XML_FORMATTER, validation.suggestion);
      }

      // Validate XML
      const parser = new DOMParser();
      const doc = parser.parseFromString(xml, 'text/xml');
      const parseError = doc.querySelector('parsererror');

      if (parseError) {
        throw new ValidationError(
          `Invalid XML: ${parseError.textContent || 'Parse error'}`,
          EncoderToolType.XML_FORMATTER,
          'Check for unclosed tags or invalid characters'
        );
      }

      // Minify XML
      const minified = xml
        .replace(/>\s+</g, '><')
        .replace(/\s+/g, ' ')
        .trim();

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.XML_FORMATTER,
        operationType: 'encode',
        inputData: xml,
        outputData: minified,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          mode: 'minify',
          originalLength: xml.length,
          minifiedLength: minified.length,
          compressionRatio: ((1 - minified.length / xml.length) * 100).toFixed(2) + '%'
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to minify XML',
        EncoderToolType.XML_FORMATTER,
        'An unexpected error occurred'
      );
    }
  }

  /**
   * Validate XML
   */
  static async validate(xml: string): Promise<EncodingOperation> {
    try {
      // Validate input
      const validation = CommonValidator.validateText(xml,
        CommonValidator.getValidationRules(EncoderToolType.XML_FORMATTER)
      );

      if (!validation.isValid) {
        throw new ValidationError(validation.error!, EncoderToolType.XML_FORMATTER, validation.suggestion);
      }

      // Try to parse XML
      const parser = new DOMParser();
      const doc = parser.parseFromString(xml, 'text/xml');
      const parseError = doc.querySelector('parsererror');

      const isValid = !parseError;
      const errorMessage = parseError ? (parseError.textContent || 'Unknown parse error') : '';

      const result = isValid
        ? `Valid XML\nRoot element: ${doc.documentElement?.tagName || 'N/A'}`
        : `Invalid XML\nError: ${errorMessage}`;

      return {
        id: this.generateOperationId(),
        toolType: EncoderToolType.XML_FORMATTER,
        operationType: 'decode',
        inputData: xml,
        outputData: result,
        timestamp: new Date(),
        status: isValid ? 'success' : 'error',
        errorMessage: isValid ? undefined : errorMessage,
        metadata: {
          mode: 'validate',
          isValid,
          length: xml.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        'Failed to validate XML',
        EncoderToolType.XML_FORMATTER,
        'An unexpected error occurred'
      );
    }
  }

  /**
   * Format XML string with indentation
   */
  private static formatXmlString(xml: string, indent: string): string {
    const reg = /(>)(<)(\/*)/g;
    let formatted = '';
    let pad = 0;

    xml = xml.replace(reg, '$1\n$2$3');
    const lines = xml.split('\n');

    for (const line of lines) {
      let currentLine = line.trim();
      if (currentLine.match(/^<\/\w/)) {
        pad--;
      }
      formatted += indent.repeat(pad) + currentLine + '\n';
      if (currentLine.match(/^<\w[^>]*[^\/]>.*$/) || currentLine.match(/^<\w[^>]*>$/)) {
        pad++;
      }
    }

    return formatted.trim();
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `xml-formatter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clears any cached data (for testing)
   */
  static clearCache(): void {
    // No-op: This service doesn't maintain cache
  }
}
